import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { XIcon } from "./Icons";

interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly closeLabel: string;
  readonly children?: ReactNode;
  readonly footer: ReactNode;
  readonly onClose: () => void;
  /** "bottom" docks the sheet to the bottom, near its trigger button. */
  readonly placement?: "center" | "bottom";
}

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

export function Modal({
  open,
  title,
  description,
  closeLabel,
  children,
  footer,
  onClose,
  placement = "center"
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.requestAnimationFrame(() => {
      const firstFocusable =
        dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      firstFocusable?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") {
      return;
    }

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []
    );
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }

    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Rendered through a portal so the fixed-position backdrop escapes any
  // ancestor stacking context (e.g. the z-indexed header or screen content)
  // and reliably covers the whole viewport, including docked footers.
  return createPortal(
    <div
      className={`modal-backdrop modal-backdrop--${placement}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-describedby={description === undefined ? undefined : descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`modal modal--${placement}`}
        onKeyDown={handleKeyDown}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <Button
            aria-label={closeLabel}
            className="icon-only-button"
            onClick={onClose}
            variant="ghost"
          >
            <XIcon />
          </Button>
        </div>
        {description === undefined ? null : (
          <p className="modal__description" id={descriptionId}>
            {description}
          </p>
        )}
        {children === undefined ? null : <div className="modal__body">{children}</div>}
        <div className="modal__footer">{footer}</div>
      </div>
    </div>,
    document.body
  );
}
